import Breadcrumbs from '@base/components/src/Breadcrumbs';
import { NextLayoutPage } from '@base/components/src/LayoutChooser/types';
import Link from '@base/components/src/Link';
import { Underline } from '@base/components/src/Link/types';
import { createQueryClient } from '@base/components/src/Query';
import { DISABLE_AFFIRM_TOTAL, DISABLE_SEZZLE_TOTAL } from '@base/components/src/Scripts/types';
import ScrollTopButton from '@base/components/src/ScrollTopButton';
import { Seo } from '@base/components/src/Seo';
import AuthApiService from '@package/core/src/auth/AuthApiService';
import AuthInteractor from '@package/core/src/auth/AuthInteractor';
import { GoogleAnalyticsCheckoutTypes } from '@package/core/src/googleAnalytics/enums';
import MenuApiInteractor from '@package/core/src/menu/MenuApiInteractor';
import MenuApiService from '@package/core/src/menu/MenuApiService';
import { PaymentMethod, PaymentMethodsType } from '@package/core/src/payment';
import PaymentMethodsApiService from '@package/core/src/payment/PaymentMethod/PaymentMethodsApiServer';
import PaymentMethodsInteractor from '@package/core/src/payment/PaymentMethod/PaymentMethodsInteractor';
import CartApiService from '@package/core/src/shoppingCart/CartApiService';
import CartInteractor from '@package/core/src/shoppingCart/CartInteractor';
import { ShoppingCard } from '@package/core/src/shoppingCart/types';
import { AUTH_USER_QUERY_DATA_KEY } from '@package/hooks/src/useAuth/types';
import useCheckoutNeedle from '@package/hooks/src/useCheckoutNeedle';
import useGoogleAnalytics from '@package/hooks/src/useGoogleAnalytics';
import { MENU_QUERY_KEY } from '@package/hooks/src/useMenu/types';
import usePaymentMethods from '@package/hooks/src/usePaymentMethods';
import { PAYMENT_METHODS_QUERY_KEY } from '@package/hooks/src/usePaymentMethods/types';
import useShoppingCart from '@package/hooks/src/useShoppingCart';
import { LabelVariantsApiService } from '@package/services/src/LabelVariants';
import { SHOPPING_CART_DATA_QUERY_KEY } from '@package/hooks/src/useShoppingCart/types';
import { TOP_SELLING_PRODUCTS } from '@package/hooks/src/useTopSellingsProducts/types';
import AlgonomyRecommendations from '@package/services/src/Algonomy/services/AlgonomyRecommendations';
import getCookieByName from '@package/utils/src/getCookieByName';
import { IsoCountryName } from '@package/utils/src/types/IsoCountryName';
import config, { LayoutType } from 'config-base';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { dehydrate } from 'react-query';
import clsx from 'clsx';
import CartEmptyState from '../../__components/CartEmptyState';
import CartProductList from '../../__components/CartProductList';
import CartSummaryDetails from '../../__components/CartSummaryDetails';
import { TwoSideLayout } from '../../layouts/_components/TwoSideLayout';
import { RenderShoppingCartLayout } from '../../layouts/ShoppingCartLayout';
import { LayoutPageProps } from '../../layouts/ShoppingCartLayout/types';
import { algonomyAnalytic, apiClient, cookieStorage, listrakAnalytic, storageManager } from '../../services';
import FreeShippings from '__components/FreeShippings';
import { LabelVariantsPages } from '@package/services/src/LabelVariants/types';
import { ShoppingCartPageProps } from './types';
import ContentApiService from '@package/core/src/content/ContentApiService';
import styles from './shoppingCart.module.css';
import { isAppleDevice } from '@package/utils/src/isIosDevice';

const authService = new AuthApiService(apiClient);
const authInteractor = new AuthInteractor(authService);

const cartService = new CartApiService(apiClient, cookieStorage, listrakAnalytic);
const cartInteractor = new CartInteractor(cartService);

const menuApiService = new MenuApiService(apiClient);
const menuInteractor = new MenuApiInteractor(menuApiService);

const paymentMethodsService = new PaymentMethodsApiService(apiClient);
const paymentMethodsInteractor = new PaymentMethodsInteractor(paymentMethodsService);

const algonomyRecommendations = new AlgonomyRecommendations(apiClient);

const contentService = new ContentApiService(apiClient);

const labelVariantsService = new LabelVariantsApiService();

const handleCheckPaymentStep = async () => {
  const disablePaymentStep = await storageManager.get('disablePaymentStep');
  if (disablePaymentStep) await storageManager.delete('disablePaymentStep');
};

const ShareShoppingCart = dynamic(() => import('../../__components/ShareShoppingCart'));

const ShoppingCart: NextLayoutPage<ShoppingCartPageProps, unknown, LayoutPageProps> = ({
  affirmEnabled,
  sezzleEnabled,
  isApplePayCapable,
}) => {
  const { cartData } = useShoppingCart();
  const { initCheckoutProcess } = useGoogleAnalytics();
  const productLabel = cartData?.cartItems == 1 ? 'Product' : 'Products';
  const isOrderKey = Boolean(getCookieByName((typeof document !== 'undefined' && document?.cookie) || '', 'order_key'));
  useCheckoutNeedle();

  const { paymentMethods } = usePaymentMethods({
    affirmEnabled: cartData?.cartSummary
      ? cartData.cartSummary.totalDue > DISABLE_AFFIRM_TOTAL && affirmEnabled
      : false,
    sezzleEnabled: cartData?.cartSummary
      ? cartData.cartSummary.totalDue > DISABLE_SEZZLE_TOTAL && sezzleEnabled
      : false,
    applePayEnabled: true,
    fetchPaymentMethods: isOrderKey,
  });

  const [showAffirm, setShowAffirm] = useState<boolean>(affirmEnabled);
  const [showSezzle, setShowSezzle] = useState<boolean>(sezzleEnabled);

  const clearShippingOptions = async () => {
    await cartInteractor.clearShippingOptions();
  };

  const addAlgonomy = (cartData: ShoppingCard) => {
    void algonomyAnalytic.recsForPlacements(cartData);
  };

  useEffect(() => {
    if (paymentMethods) {
      setShowAffirm(paymentMethods?.some((method: PaymentMethod) => method.paymentType === PaymentMethodsType.Affirm));
      setShowSezzle(paymentMethods?.some((method: PaymentMethod) => method.paymentType === PaymentMethodsType.Sezzle));
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (cartData) {
      void initCheckoutProcess({
        shoppingCart: cartData,
        checkoutType: GoogleAnalyticsCheckoutTypes.ShoppingCart,
      });
      if (isOrderKey) {
        void clearShippingOptions();
      }
    }
    if (cartData && cartData?.subOrders?.length > 0) {
      addAlgonomy(cartData);
    }
  }, [cartData]);

  useEffect(() => {
    void handleCheckPaymentStep();
  }, []);

  return (
    <>
      {cartData && cartData.subOrders.length > 0 ? (
        <main className={`${styles.mainContainer} ${styles.ordersContainer}`}>
          <section className={styles.breadcrumbsContainer}>
            <Breadcrumbs
              id="breadcrumbs"
              separator={config.brandCode === '2' ? '//' : '>'}
              breadcrumbsContainerClassName="cartBreadcrumbs"
            >
              <Link underline={Underline.none} href={config.mainPyramydairUrl} showCursorPointer={true}>
                Home
              </Link>
              <p>My Cart</p>
            </Breadcrumbs>
          </section>
          <section
            className={clsx(styles.container, {
              [styles.shoppingCartAGD]: config.brandCode === '2',
            })}
          >
            <h1 id="shopping-cart-page-title" className={styles.title}>
              <span className={styles.mainTitle}>Shopping Cart</span>
              {cartData?.cartItems !== 0 && (
                <span
                  id="cart-items-label"
                  className={clsx(styles.subTitle, {
                    [styles.subTitleAGD]: config.brandCode === '2',
                  })}
                >{`${cartData?.cartItems} ${productLabel}`}</span>
              )}
            </h1>
            <FreeShippings cartData={cartData} />
            <div className={styles.cartContainer}>
              <TwoSideLayout
                rightSideClass={styles.cartSummary}
                leftSide={
                  <div id="shopping-cart-product-list">
                    <CartProductList
                      subOrders={cartData.subOrders}
                      checkoutButtonAvailable={cartData.cartSummary.checkoutButtonAvailable}
                      subTotal={cartData?.cartSummary?.subTotal}
                      regularCouponApplied={cartData.regularCouponApplied}
                    />
                    {cartData.adminUser && <ShareShoppingCart />}
                  </div>
                }
                rightSide={
                  <CartSummaryDetails
                    summary={cartData.cartSummary}
                    showAffirm={showAffirm}
                    showSezzle={showSezzle}
                    paymentMethods={paymentMethods}
                  />
                }
              />
            </div>
          </section>
        </main>
      ) : (
        <main className={`${styles.mainContainer} ${styles.emptyContainer}`}>
          <CartEmptyState />
        </main>
      )}
      <ScrollTopButton />
    </>
  );
};

ShoppingCart.renderLayout = RenderShoppingCartLayout(
  <Seo noindex title={`Shopping Cart |  ${config.brandCode === '2' ? 'Airgun Depot' : 'Pyramyd AIR'}`} />,
);

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const queryClient = createQueryClient();

  await queryClient.prefetchQuery([AUTH_USER_QUERY_DATA_KEY], async () => {
    const res = await authInteractor.getUser({
      cookie: context.req.headers.cookie,
    });
    return res.data;
  });

  await queryClient.prefetchQuery([SHOPPING_CART_DATA_QUERY_KEY], async () => {
    const res = await cartInteractor.getShoppingCard({
      cookie: `${context.req.headers.cookie}; mp_clear_payments=true;`,
    });

    if (res.data && res?.data?.cookies) {
      const orderKey = getCookieByName(res.data.cookies, 'order_key');
      context.res.setHeader('set-cookie', `order_key=${orderKey}; Max-Age=${60 * 60 * 24 * 7}; path=/;`);
    }

    return res.data;
  });

  await queryClient.prefetchQuery([TOP_SELLING_PRODUCTS], async () => {
    const topPropducts = await algonomyRecommendations.getTopSellings();
    return topPropducts?.data;
  });

  await queryClient.prefetchQuery([MENU_QUERY_KEY], async () => {
    const res = await menuInteractor.getMenuData();
    return res;
  });

  await queryClient.prefetchQuery([PAYMENT_METHODS_QUERY_KEY], async () => {
    const res = await paymentMethodsInteractor.getPaymentMethods({
      cookie: `${context.req.headers.cookie}; mp_clear_payments=true;`,
    });

    return res.data;
  });

  const labels = await labelVariantsService.getPageLabelVariants(LabelVariantsPages.SHOPPING_CART);
  const freeSHTotalRetail = await contentService.getFreeSHTotalRetail();

  const userAgent = context.req.headers['user-agent'] || '';
  const isApplePayCapable = isAppleDevice(userAgent);

  return {
    props: {
      layoutType: LayoutType.Main,
      dehydratedState: dehydrate(queryClient),
      labels,
      affirmEnabled: context.req?.headers['cf-ipcountry'] === IsoCountryName.US,
      sezzleEnabled: context.req?.headers['cf-ipcountry'] === IsoCountryName.US,
      freeSHTotalRetail: freeSHTotalRetail.data,
      isApplePayCapable,
    },
  };
}

export default ShoppingCart;
